import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getAccountDetails } from '../../../store/selectors/account';
import { useErrorToaster } from '../../../utils/errorToaster';
import { savePersonalDetails } from '../../../store/actions/account';
import AddressInput from '../../shared/AddressInput';

function UserDetails() {
  const { t } = useTranslation(['account', 'home', 'common', 'validation']);
  const tenant = useSelector(state => state.tenant);
  const dispatch = useDispatch();
  const accountDetails = useSelector(getAccountDetails);
  const displayError = useErrorToaster();
  const ref = React.useRef(null);
  const [addressPicked, setAddressPicked] = useState(false);
  const [customAddressPicked, setCustomAddressPicked] = useState(false);

  const initialValues = {
    firstname: ref && ref.current ? ref.current.values.firstname : '',
    lastname: ref && ref.current ? ref.current.values.lastname : '',
    address: accountDetails.address,
    addressLine2: accountDetails.addressLine2,
    phoneNumber: ref && ref.current ? ref.current.values.phoneNumber : ''
  };

  const validationPhone = Yup.object({
    phoneNumber: Yup.string().required(t('validation:required'))
  });

  const validation = Yup.object({
    firstname: Yup.string().required(t('validation:required')),
    lastname: Yup.string().required(t('validation:required')),
    address: Yup.string().required(t('validation:required'))
  });

  const validationSchema = () => {
    let schema = Yup.object({});

    schema = schema.concat(validation);
    if (tenant.registrationSteps.includes('driver_on_sms_form'))
      schema = schema.concat(validationPhone);

    return schema;
  };

  const handleFormSubmit = async (
    { firstname, lastname, address, addressLine2, phoneNumber },
    { setSubmitting }
  ) => {
    try {
      await dispatch(
        savePersonalDetails(
          {
            firstname,
            lastname,
            address,
            addressLine2,
            phoneNumber
          },
          customAddressPicked
        )
      );
    } catch (error) {
      setSubmitting(false);
      displayError(error);
    }
  };

  const handleLockButtonSuggestionsPicked = addressPicked => {
    setAddressPicked(addressPicked);
  };

  const handleCustomAdressPicked = customAddressPicked => {
    setCustomAddressPicked(customAddressPicked);
  };

  return (
    <div className="personal-details">
      <Typography component="h1">{t('account:your-details')}</Typography>

      <Formik
        innerRef={ref}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          handleChange,
          isSubmitting,
          errors,
          touched,
          setFieldValue
        }) => (
          <Form>
            <div className="form-group">
              <TextField
                id="lastname"
                name="lastname"
                placeholder={t('account:lastname')}
                variant="outlined"
                margin="none"
                value={values.lastname}
                onChange={handleChange}
                error={touched.lastname && !!errors.lastname}
                helperText={
                  touched.lastname && errors.lastname ? errors.lastname : null
                }
                fullWidth
              />
            </div>

            <div className="form-group">
              <TextField
                id="firstname"
                name="firstname"
                placeholder={t('account:firstname')}
                variant="outlined"
                margin="none"
                value={values.firstname}
                onChange={handleChange}
                error={touched.firstname && !!errors.firstname}
                helperText={
                  touched.firstname && errors.firstname
                    ? errors.firstname
                    : null
                }
                fullWidth
              />
            </div>

            <div className="form-group">
              <AddressInput
                name="address"
                placeholder={t('account:address')}
                value={values.address}
                disabled={customAddressPicked}
                onChange={value => setFieldValue('address', value)}
                error={touched.address && !!errors.address}
                handleLockButtonSuggestionsPicked={
                  handleLockButtonSuggestionsPicked
                }
                handleCustomAdressPicked={handleCustomAdressPicked}
                helperText={
                  touched.address && errors.address ? errors.address : null
                }
              />
            </div>

            <div className="form-group">
              <TextField
                variant="outlined"
                margin="none"
                label={t('account:additional-address')}
                name="addressLine2"
                placeholder={t('account:additional-address-placeholder')}
                value={values.addressLine2 ? values.addressLine2 : ''}
                onChange={handleChange}
                error={touched.addressLine2 && !!errors.addressLine2}
                helperText={
                  touched.addressLine2 && errors.addressLine2
                    ? errors.addressLine2
                    : null
                }
                fullWidth
              />
            </div>

            {tenant.registrationSteps.includes('driver_on_sms_form') ? (
              <div className="form-group">
                <TextField
                  name="phoneNumber"
                  type="tel"
                  placeholder={t('account:phone-number')}
                  variant="outlined"
                  margin="none"
                  value={values.phoneNumber}
                  onChange={handleChange}
                  error={touched.phoneNumber && !!errors.phoneNumber}
                  helperText={
                    touched.phoneNumber && errors.phoneNumber
                      ? errors.phoneNumber
                      : t('account:phone-number-helper')
                  }
                  fullWidth
                />
              </div>
            ) : null}

            <div className="button-group">
              <Button
                type="submit"
                fullWidth
                disabled={isSubmitting || !addressPicked}
                variant="contained"
                color="primary"
              >
                {t('common:next')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default UserDetails;
